import { create } from 'zustand';
import { config } from '@/config/env';
import { getAccessToken } from '@/auth/supabaseClient';

export interface InventoryItem {
  itemId: string;
  name: string;
  itemType: string;
  owned: boolean;
  equipped: boolean;
}

interface InventoryState {
  items: InventoryItem[];
  loading: boolean;
  error: string | null;

  fetchInventory: () => Promise<void>;
  equipItem: (itemId: string) => Promise<void>;
  startCheckout: (itemId: string) => Promise<string>;
}

export const useInventoryStore = create<InventoryState>((set, get) => ({
  items: [],
  loading: false,
  error: null,

  fetchInventory: async () => {
    set({ loading: true, error: null });
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${config.apiBaseUrl}/inventory`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch inventory');
      }

      const data = await res.json();
      const items: InventoryItem[] = data.items.map((item: {
        item_id: string;
        name: string;
        item_type: string;
        owned: boolean;
        equipped: boolean;
      }) => ({
        itemId: item.item_id,
        name: item.name,
        itemType: item.item_type,
        owned: item.owned,
        equipped: item.equipped,
      }));

      set({ items, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  equipItem: async (itemId: string) => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const res = await fetch(`${config.apiBaseUrl}/inventory/equip`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ item_id: itemId }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to equip item');
    }

    // Update local state: equip target, unequip others of same type
    const targetItem = get().items.find(i => i.itemId === itemId);
    const targetType = targetItem?.itemType;
    const items = get().items.map(item => ({
      ...item,
      equipped: item.itemId === itemId
        ? true
        : (targetType && item.itemType === targetType ? false : item.equipped),
    }));

    set({ items });
  },

  startCheckout: async (itemId: string): Promise<string> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const res = await fetch(`${config.apiBaseUrl}/payments/checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ item_id: itemId }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to start checkout');
    }

    const data = await res.json();
    return data.url;
  },
}));
